import axios from "axios";
import API_URLS from "../../../config/global_variables";

const authToken = () => {
  try {
    return JSON.parse(localStorage.userData || "{}").token || "";
  } catch {
    return "";
  }
};

const headers = () => ({
  "Content-Type": "application/json",
  Authorization: authToken(),
});

const ERROR_MESSAGES = {
  VALIDATION_ERROR: "Please fix the highlighted fields.",
  NOT_FOUND: "That keyword no longer exists.",
  DUPLICATE_KEY: "This keyword already exists for this blog.",
  SERVER_ERROR: "Something went wrong. Please try again.",
};

const mapError = (err) => {
  const apiErr = err?.response?.data?.error;
  const code = apiErr?.code;
  const status = err?.response?.status;
  const fallback =
    apiErr?.message ||
    (status ? `Request failed (${status})` : err?.message || ERROR_MESSAGES.SERVER_ERROR);
  let message = ERROR_MESSAGES[code] || fallback;
  if (code === "VALIDATION_ERROR" && Array.isArray(apiErr?.details) && apiErr.details.length) {
    message = apiErr.details.map((d) => `${d.path || "field"}: ${d.message}`).join("\n");
  }
  return {
    code: code || (status ? `HTTP_${status}` : "NETWORK_ERROR"),
    status,
    message,
    details: apiErr?.details || [],
  };
};

const baseUrl = (blogId) => API_URLS.KEYWORD_POINTS(blogId);
const upsertUrl = (blogId) => API_URLS.KEYWORD_POINTS_UPSERT(blogId);

const listKeywordPoints = async (blogId) => {
  const res = await axios.get(baseUrl(blogId), { headers: headers() });
  const payload = res?.data?.data || {};
  return {
    blogId: payload.blogId,
    count: payload.count ?? (payload.items || []).length,
    items: payload.items || [],
  };
};

const createKeywordPoint = async (blogId, body) => {
  const res = await axios.post(baseUrl(blogId), body, { headers: headers() });
  return res?.data?.data || null;
};

const updateKeywordPoint = async (blogId, id, body) => {
  const res = await axios.put(`${baseUrl(blogId)}/${encodeURIComponent(String(id))}`, body, {
    headers: headers(),
  });
  return res?.data?.data || null;
};

const deleteKeywordPoint = async (blogId, id) => {
  const res = await axios.delete(
    `${baseUrl(blogId)}/${encodeURIComponent(String(id))}`,
    { headers: headers() }
  );
  return res?.data?.data || null;
};

const bulkUpdateKeywordPoints = async (blogId, items) => {
  const res = await axios.put(
    `${baseUrl(blogId)}/bulk`,
    { items },
    { headers: headers() }
  );
  const payload = res?.data?.data || {};
  return {
    blogId: payload.blogId,
    updated: payload.updated ?? (payload.items || []).length,
    items: payload.items || [],
  };
};

// Bulk upsert by tag_id: PUT /:blogId/keyword-points/bulk-upsert
// items: [{ tag_id, points }]
// Returns { blogId, count, items[] }
const bulkUpsertKeywordPoints = async (blogId, items) => {
  const res = await axios.put(
    upsertUrl(blogId),
    { items },
    { headers: headers() }
  );
  const payload = res?.data?.data || {};
  return {
    blogId: payload.blogId,
    count: payload.count ?? (payload.items || []).length,
    items: payload.items || [],
  };
};

// Update a single keyword-point by kpId (new backend endpoint from bug fix)
// PUT /api/v1/admin/rec-blogs/:blogId/keyword-points/:kpId
// body: { keyword, points }
const updateSingleRecBlogKp = async (blogId, kpId, { keyword, points }) => {
  const res = await axios.put(
    API_URLS.REC_BLOG_KP_UPDATE(blogId, kpId),
    { keyword, points },
    { headers: headers() }
  );
  return res?.data?.data || null;
};

// Bulk insert: POST /:blogId/keyword-points/bulk
// items: [{ keyword, points }]
// Returns { blogId, inserted, items[] }
const bulkCreateKeywordPoints = async (blogId, items) => {
  const res = await axios.post(
    `${baseUrl(blogId)}/bulk`,
    { items },
    { headers: headers() }
  );
  const payload = res?.data?.data || {};
  return {
    blogId: payload.blogId,
    inserted: payload.inserted ?? (payload.items || []).length,
    items: payload.items || [],
  };
};

const apiHelpers = {
  listKeywordPoints,
  createKeywordPoint,
  updateKeywordPoint,
  updateSingleRecBlogKp,
  deleteKeywordPoint,
  bulkUpdateKeywordPoints,
  bulkCreateKeywordPoints,
  bulkUpsertKeywordPoints,
  mapError,
};

export default apiHelpers;
